import React, { useState, useEffect } from 'react';
import { Settings, Loader2, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = [
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar (CA$)' },
  { code: 'USD', symbol: '$', label: 'US Dollar ($)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (£)' },
  { code: 'EUR', symbol: '€', label: 'Euro (€)' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar (A$)' },
  { code: 'INR', symbol: '₹', label: 'Indian Rupee (₹)' },
  { code: 'PKR', symbol: 'Rs ', label: 'Pakistani Rupee (Rs)' },
];

const AdminShopSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    shop_name: '', shop_description: '', tax_rate: 13, flat_shipping: 9.99,
    free_shipping_threshold: 75, currency: 'CAD', currency_symbol: 'CA$', shop_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shop/settings`, { headers });
      const data = await res.json();
      setSettings({ ...settings, ...data });
    } catch { } finally { setLoading(false); }
  };

  const handleCurrencyChange = (code) => {
    const cur = CURRENCIES.find((c) => c.code === code);
    setSettings((s) => ({ ...s, currency: code, currency_symbol: cur?.symbol || code }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/shop/settings`, { method: 'PUT', headers, body: JSON.stringify(settings) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast({ title: 'Settings saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-pink-500" /></div>;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-7 h-7 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shop Settings</h1>
          <p className="text-gray-500 text-sm">Configure tax, shipping, currency, and more</p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6 max-w-2xl">
        {/* General */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-gray-900 mb-4">General</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Shop Name</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" value={settings.shop_name} onChange={(e) => setSettings((s) => ({ ...s, shop_name: e.target.value }))} data-testid="shop-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Shop Description</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300 h-20 resize-none" value={settings.shop_description} onChange={(e) => setSettings((s) => ({ ...s, shop_description: e.target.value }))} />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="shop-enabled" checked={settings.shop_enabled} onChange={(e) => setSettings((s) => ({ ...s, shop_enabled: e.target.checked }))} className="rounded" data-testid="shop-enabled" />
              <label htmlFor="shop-enabled" className="text-sm text-gray-700">Shop is enabled (visible to users)</label>
            </div>
          </div>
        </div>

        {/* Currency */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-gray-900 mb-4">Currency</h2>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
            <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" value={settings.currency} onChange={(e) => handleCurrencyChange(e.target.value)} data-testid="shop-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Tax & Shipping */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="font-bold text-gray-900 mb-4">Tax & Shipping</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tax Rate (%)</label>
              <input type="number" step="0.1" min="0" max="100" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" value={settings.tax_rate} onChange={(e) => setSettings((s) => ({ ...s, tax_rate: parseFloat(e.target.value) }))} data-testid="tax-rate" />
              <p className="text-xs text-gray-400 mt-1">e.g. 13 for Ontario HST</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Flat Shipping Fee ({settings.currency_symbol})</label>
              <input type="number" step="0.01" min="0" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" value={settings.flat_shipping} onChange={(e) => setSettings((s) => ({ ...s, flat_shipping: parseFloat(e.target.value) }))} data-testid="flat-shipping" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Free Shipping Threshold ({settings.currency_symbol})</label>
              <input type="number" step="0.01" min="0" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" value={settings.free_shipping_threshold} onChange={(e) => setSettings((s) => ({ ...s, free_shipping_threshold: parseFloat(e.target.value) }))} data-testid="free-shipping-threshold" />
              <p className="text-xs text-gray-400 mt-1">Orders above this get free shipping</p>
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors disabled:opacity-50" data-testid="save-settings-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Settings</>}
        </button>
      </form>
    </div>
  );
};

export default AdminShopSettings;
